/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unnecessary-type-conversion, @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-invalid-void-type, @typescript-eslint/no-non-null-assertion, unused-imports/no-unused-vars -- API slice boundary: endpoints handle blob downloads, legacy response payloads, and dynamic role-aware routes. */
import { buildRoleAwarePrefix, isAdvisorRole } from '@/utils/api';
import { Role } from '@/models/AccountEnums';
import {
  triggerBlobDownload,
  createBlobForFile,
  createTypedBlob,
} from '@/utils/triggerBlobDownload';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  createApi,
  FetchBaseQueryError,
  type TypedUseQueryHookResult,
  type TypedUseQuerySubscriptionOptions,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '../models/ServiceTypes';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';
import { openFileFromBlob } from '@/utils/nativePDFHandling';
import {
  getMimeTypeFromFilename,
  getMimeTypeFromContentType,
  MimeType,
} from '@/utils/fileValidation';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';
import {
  mergeViewingTagIntoArg,
  type ViewingTagSelection,
} from '@/utils/viewingTagSelection';
import { selectQueryViewingTag } from './selectQueryViewingTag';

const handleBlobResponse = async (response: Response) => {
  if (response.ok) {
    if (Capacitor.getPlatform() !== 'web') {
      return await response.arrayBuffer();
    } else {
      return await response.blob();
    }
  }
  // Attempt to parse error as text, which is always serializable
  return response.text();
};

async function openDocumentPreview(
  data: BlobPart | ArrayBuffer | Blob,
  fileName: string,
): Promise<void> {
  let mimeType = getMimeTypeFromFilename(fileName);

  if (mimeType === MimeType.OctetStream && data instanceof Blob) {
    const fromResponse = getMimeTypeFromContentType(data.type);
    if (fromResponse) {
      mimeType = fromResponse;
    }
  }

  const blob =
    data instanceof Blob && data.type === (mimeType as string)
      ? data
      : createTypedBlob(
          data instanceof Blob ? await data.arrayBuffer() : data,
          fileName,
          mimeType,
        );

  await openFileFromBlob(blob, fileName, mimeType);
}

function buildDocuments2FileUrl(
  currentRole: Role | undefined,
  vaultId: string | number,
  investorId: number | undefined,
): string {
  if (isAdvisorRole(currentRole) && typeof investorId === 'number') {
    return `/api/advisor/investors/${String(investorId)}/Documents2/${String(vaultId)}`;
  }
  return `/api/${String(currentRole)}/Documents2/${String(vaultId)}`;
}

async function triggerDocumentDownload(
  data: BlobPart | ArrayBuffer,
  filename: string,
): Promise<void> {
  const mimeType = getMimeTypeFromFilename(filename);
  const blob = createBlobForFile(data, filename);
  await triggerBlobDownload(blob, filename, mimeType);
}

// Export DocumentPermission
export interface DocumentPermission {
  AdvisorId: number;
  AdvisorName: string;
  AllowedToSee: boolean;
  CanEdit: boolean;
}

// --- "Share Document With": household sharing (EUD-434) ---
// Shape of `InvestorDocumentHouseholdPermissionModel` and friends in
// WealthAccessApiService (Processors/Investor/Models/Models).
export interface DocumentHouseholdMember {
  Name: string;
  UserName?: string | null;
}

export interface DocumentHouseholdPermission {
  InvestorHHId: number;
  HouseholdName: string;
  CanSee: boolean;
  Members: DocumentHouseholdMember[];
}

export interface DocumentHouseholdPermissionRecord {
  VaultFileId: number;
  Name: string;
  IsFolder: boolean;
  Extension: string;
  Permissions: DocumentHouseholdPermission[];
}

// Define the structure for associations in the PUT request
// Based on the cURL example, it seems flexible
interface DocumentAssociation {
  WealthAccountId?: number;
  WealthId?: number;
  VaultFileId: string; // Needs the current VaultFileId
  // Add other potential fields if needed based on API requirements
  // InvestorId?: number;
  // Name?: string;
  // VaultFileAssociationId?: number; // Likely not needed for PUT/update
}

export interface NetworkedDocumentRecord {
  CanSign: boolean;
  IsSignatureRequested: boolean;
  IsSigned: boolean;
  Associations: DocumentAssociation[];
  CanDelete: boolean;
  CanMove: boolean;
  CanRename: boolean;
  Extension: string;
  FileSize: number;
  IsFolder: boolean;
  Name: string;
  NumberOfFiles: number;
  NumberOfFolders: number;
  Permissions: DocumentPermission[];
  UploadDate: string; // Consider using Date type if needed
  VaultFileId: string;
  AddedById?: number; // Optional fields based on example
  AddedByName?: string; // Optional fields based on example
  ParentId?: number | null; // Added ParentId based on PUT example
  AccountId?: number | null; // Added AccountId based on PUT example
  LastReadDate?: string; // Added LastReadDate based on PUT example
}

export interface DocumentRecord extends NetworkedDocumentRecord {
  id: number;
}

// History record type (basic fields based on expected usage)
export interface DocumentHistoryRecord {
  VaultFileId?: number | string;
  Name?: string;
  LastOpened?: string;
  UploadedByName?: string;
  UploadDate?: string;
}

// --- Types for Document Folders ---
export interface DocumentFolderRecord {
  Name: string;
  Level: number;
  Children: DocumentFolderRecord[]; // Recursive structure
  VaultFileId?: number; // Optional, as root might not have it
  ParentId?: number; // Optional
}

// The response structure matches what we need, so no separate "Network" type strictly needed
// unless we want to add an 'id' field later for consistency.
// For now, using DocumentFolderRecord directly in RecordResponse.
export type DocumentFoldersResponse = RecordResponse<DocumentFolderRecord>;

// Args type, similar to GetDocumentsArgs
interface GetDocumentFoldersArgs {
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  _?: string; // Cache busting parameter
}
// --- End Types for Document Folders ---

type DocumentsResponse = RecordResponse<DocumentRecord>;

type NetworkDocumentsResponse = RecordResponse<NetworkedDocumentRecord>;
type UploadDocumentResponse = NetworkedDocumentRecord;

export interface GetDocumentsArgs {
  SearchTerm?: string;
  sort?: Array<{
    property: string;
    direction: 'asc' | 'desc';
    order: number;
  }>;
  folderId?: number; // Optional: Add if needed to fetch documents within a specific folder
  _?: string; // Cache busting parameter
  InvestorId?: number; // Optional investor-scoped query param
  InvestorTagId?: number;
  TagId?: number;
}

interface UploadDocumentArgs {
  file: File;
  folderId?: string;
  accountId?: number;
  InvestorId?: number;
}

interface CreateFolderArgs {
  Name: string;
  parentFolderId?: string; // Optional: if API supports creating subfolders
  sort?: GetDocumentsArgs['sort'];
  InvestorTagId?: number;
  TagId?: number;
  InvestorId?: number;
}

// Response is likely the ID of the created folder
type CreateFolderResponse = string | number;

// --- Types for Documents2 Query ---
interface NetworkDocument2Record {
  VaultFile2Id: number;
  Name: string;
  FileName: string;
  Extension: string;
  Encryption: string; // Be careful with sensitive data like this
  FileSize: number;
  VaultFileCategoryId: number;
  UploadDate: string; // Consider Date type
  StatementDate?: string;
  WealthAccountId?: number | null;
  Label: string;
  InvestorId: number;
  UserId: number;
  IsVisible: boolean;
  UploadedBy: string;
  VendorFileId?: string;
}

type NetworkDocuments2Response = NetworkDocument2Record[];

export interface GetDocuments2Args {
  DateInterval?: number | string; // e.g., -1 for default
  WealthAccountId: number | string | undefined; // e.g., "" for default
  // Add other potential parameters if needed
  VaultFileCategoryId?: number | string;
  FileSourceId?: number | string;
  SearchTerm?: string;
  AdvisorInvestorId?: number;
  InvestorTagId?: number;
  TagId?: number;
}
// --- End Types for Documents2 Query ---

// Type for the transformed response
export type Document2Record = NetworkDocument2Record & {
  id: number | string;
};
type Documents2Response = Document2Record[];

export interface AdvisorRecentDocumentRecord {
  VaultFileId: number;
  InvestorId: number;
  InvestorName: string;
  Label: string;
  Extension: string | null;
  UploadDate: string;
  ParentId: number | null;
  ParentName: string | null;
}

export interface GetAdvisorRecentDocumentsArgs {
  StartDate: string;
  EndDate: string;
}

// Define args for download mutation
interface DownloadDocumentArgs {
  vaultId: string | number;
  filename: string;
  statementId?: string;
  statementName?: string;
  InvestorId?: number;
  signal?: AbortSignal;
}

// Define success response type for download mutation
interface DownloadDocumentResponse {
  success: boolean; // Indicate success
  filename: string; // Keep filename for potential feedback
}

// Args for preview mutation
interface PreviewDocumentArgs {
  vaultId: string | number;
  statementId?: string;
  statementName?: string;
  InvestorId?: number;
  fileName: string;
}

// Define args for bulk download mutation
interface BulkDownloadDocumentsArgs {
  vaultFileIds: string[];
  filename?: string;
  signal?: AbortSignal;
}

export interface HistoricalTaxExternalFile {
  externalFileId: string;
  externalFileName: string;
}

interface BulkDownloadDocuments2Args {
  vaultFileIds: string[];
  personalVaultFileIds?: string[];
  externalFiles?: HistoricalTaxExternalFile[];
  investorId?: number;
  filename?: string;
  signal?: AbortSignal;
}

// Define success response type for bulk download mutation
interface BulkDownloadDocumentsResponse {
  success: boolean;
  filename: string;
}

// --- Types for Update Document Mutation ---
interface UpdateDocumentArgs {
  // Include all fields needed in the PUT request body
  VaultFileId: string; // Used in URL and potentially body
  Name: string;
  ParentId: number | null;
  Permissions: DocumentPermission[];
  Associations: DocumentAssociation[];
  AccountId: number | null | undefined;
  InvestorId?: number;
  // Include other fields from the full NetworkedDocumentRecord that the API expects
  // It's often necessary to send back the whole object or most of it
  CanSign: boolean;
  IsSignatureRequested: boolean;
  IsSigned: boolean;
  AddedById?: number;
  AddedByName?: string;
  CanDelete: boolean;
  CanMove: boolean;
  CanRename: boolean;
  Extension: string;
  FileSize: number;
  IsFolder: boolean;
  LastReadDate?: string;
  NumberOfFiles: number;
  NumberOfFolders: number;
  UploadDate: string;
}

// Response is boolean based on example
type UpdateDocumentResponse = boolean;
// --- End Types for Update Document Mutation ---

interface GetDocumentsByAccountIdArgs {
  accountId: number;
  // Include other relevant filters from GetDocumentsArgs if needed
  SearchTerm?: string;
  sort?: GetDocumentsArgs['sort'];
  InvestorId?: number;
}

export function mergeViewingInvestorTagIntoDocumentsArg(
  arg: GetDocumentsArgs,
  selection: ViewingTagSelection,
): GetDocumentsArgs {
  if ('InvestorId' in arg && arg.InvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildDocumentsSubscriptionArg(
  arg: GetDocumentsArgs,
  selection: ViewingTagSelection,
): GetDocumentsArgs {
  return mergeViewingInvestorTagIntoDocumentsArg(arg, selection);
}

export function buildCreateFolderArg(
  arg: CreateFolderArgs,
  selection: ViewingTagSelection,
  hasTagSelectorAccess = true,
): CreateFolderArgs {
  // Match list queries: without tag-selector access, omit tag ids even if Redux
  // still holds a stale viewing-tag selection.
  return mergeViewingTagIntoArg(arg, hasTagSelectorAccess ? selection : null);
}

export function mergeViewingInvestorTagIntoDocuments2Arg(
  arg: GetDocuments2Args,
  selection: ViewingTagSelection,
): GetDocuments2Args {
  if ('AdvisorInvestorId' in arg && arg.AdvisorInvestorId !== undefined) {
    return arg;
  }
  return mergeViewingTagIntoArg(arg, selection);
}

export function buildDocuments2SubscriptionArg(
  arg: GetDocuments2Args,
  selection: ViewingTagSelection,
): GetDocuments2Args {
  return mergeViewingInvestorTagIntoDocuments2Arg(arg, selection);
}

interface UpdateDocumentForAccountArgs extends UpdateDocumentArgs {
  accountId: number;
}

interface PreviewAccountDocumentArgs {
  wealthAccountId: number;
  vaultId: string | number;
  fileName: string;
  InvestorId?: number;
}

interface DownloadAccountDocumentArgs {
  wealthAccountId: number;
  vaultId: string | number;
  filename: string;
  InvestorId?: number;
}

interface DeleteDocumentForAccountArgs {
  accountId: number;
  vaultId: string | number;
  InvestorId?: number;
}

interface DocumentsCountResponse {
  count: number;
}

/**
 * API service for document-related endpoints
 */
export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'Documents',
    'Documents2',
    'DocumentFolders',
    'DocumentHouseholdPermissions',
  ],

  endpoints: (builder) => ({
    // Documents History (role-aware: investor vs advisor/investors/{id})
    getDocumentsHistory: builder.query<
      RecordResponse<DocumentHistoryRecord>,
      { InvestorId?: number } | void
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const investorId =
          args && 'InvestorId' in (args as any)
            ? ((args as any).InvestorId as number | undefined)
            : undefined;

        let url: string;
        if (
          (currentRole || '').toLowerCase() === 'advisor' &&
          typeof investorId === 'number'
        ) {
          url = `/api/advisor/investors/${investorId}/documentsHistory`;
        } else {
          url = `/api/investor/DocumentsHistory`;
        }

        const params: Record<string, any> = {
          'sort[0].Property': 'LastOpened',
          'sort[0].Direction': 'desc',
          'sort[0].Order': 0,
        };

        const result = await baseQuery({ url, method: 'GET', params });
        if (result.error) return { error: result.error };

        // Normalize to RecordResponse shape
        const raw = result.data as
          | RecordResponse<DocumentHistoryRecord>
          | DocumentHistoryRecord[];
        if (Array.isArray(raw)) {
          return {
            data: {
              Records: raw,
              TotalLength: raw.length,
              SummedValue: null,
            },
          };
        }
        return { data: raw };
      },
      providesTags: (_res) => [{ type: 'Documents', id: 'HISTORY' }],
    }),
    getDocuments: builder.query<DocumentsResponse, GetDocumentsArgs | void>({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Base URL (role-aware + optional investor scoping)
        const url = `${buildRoleAwarePrefix(currentRole, args?.InvestorId)}/documents/`;

        const params: Record<string, any> = {};

        // Handle sort parameters
        if (args?.sort && args.sort.length > 0) {
          args.sort.forEach((sortItem, index) => {
            params[`sort[${index}].Property`] = sortItem.property;
            params[`sort[${index}].Direction`] = sortItem.direction;
            params[`sort[${index}].Order`] = sortItem.order;
          });
        }

        params.ParentId = args?.folderId || '';

        params.SearchTerm = args?.SearchTerm || '';
        if (typeof args?.InvestorId === 'number') {
          params.investorId = args.InvestorId;
        }
        if (typeof args?.InvestorTagId === 'number') {
          params.InvestorTagId = args.InvestorTagId;
        }
        if (typeof args?.TagId === 'number') {
          params.TagId = args.TagId;
        }
        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          console.log('DOCUMENTS API RESULT:', {
            isError: !!result.error,
            hasData: !!result.data,
            paramsSent: params,
            urlSent: url,
            dataLength:
              (result.data as NetworkDocumentsResponse)?.Records?.length ??
              'N/A',
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('DOCUMENTS API ERROR', result.error);
            return { error: result.error };
          }

          if (!result.data) {
            console.error('DOCUMENTS API RETURNED NO DATA');
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'No data returned from API' },
                error: 'No data returned from API',
              } as FetchBaseQueryError,
            };
          }

          // Basic check for expected structure
          const networkResponse = result.data as NetworkDocumentsResponse;
          if (!networkResponse.Records) {
            console.warn('DOCUMENTS API RETURNED UNEXPECTED DATA STRUCTURE');
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'Unexpected data structure from API' },
                error: 'Unexpected data structure',
              } as FetchBaseQueryError,
            };
          }

          // --- Transformation Step ---
          const transformedRecords: DocumentRecord[] =
            networkResponse.Records.map((doc) => ({
              ...doc,
              // Assuming VaultFileId is a string that can be parsed to a number
              // If VaultFileId might not be a valid number, add error handling/default value
              id: parseInt(doc.VaultFileId, 10) || 0, // Map VaultFileId to id (as number)
            }));

          const transformedData: DocumentsResponse = {
            ...networkResponse,
            Records: transformedRecords,
          };
          // --- End Transformation Step ---

          return { data: transformedData }; // Return the transformed data
        } catch (err) {
          console.error('DOCUMENTS API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (result) =>
        result?.Records
          ? [
              ...result.Records.map(({ id }) => ({
                type: 'Documents' as const,
                id: id,
              })),
              { type: 'Documents', id: 'LIST' },
            ]
          : [{ type: 'Documents', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
      keepUnusedDataFor: 500,
    }),
    // Get Documents by Account ID Query
    getDocumentsByAccountId: builder.query<
      DocumentsResponse,
      GetDocumentsByAccountIdArgs
    >({
      queryFn: async (
        { accountId, InvestorId, ...args },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // This endpoint is specific to an account
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/Accounts/${accountId}/documents`;

        const params: Record<string, any> = {};

        // Handle sort and search parameters similarly to getDocuments
        if (args?.sort && args.sort.length > 0) {
          args.sort.forEach((sortItem, index) => {
            params[`sort[${index}].Property`] = sortItem.property;
            params[`sort[${index}].Direction`] = sortItem.direction;
            params[`sort[${index}].Order`] = sortItem.order;
          });
        }
        params.SearchTerm = args?.SearchTerm || '';

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          if (result.error) {
            console.error(
              `DOCUMENTS BY ACCOUNT API ERROR for account ${accountId}`,
              result.error,
            );
            return { error: result.error };
          }

          if (!result.data) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'No data returned from API' },
              } as FetchBaseQueryError,
            };
          }

          const networkResponse = result.data as NetworkDocumentsResponse;
          if (!networkResponse.Records) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'Unexpected data structure from API' },
              } as FetchBaseQueryError,
            };
          }

          // Transform data to include 'id' field
          const transformedRecords: DocumentRecord[] =
            networkResponse.Records.map((doc) => ({
              ...doc,
              id: parseInt(doc.VaultFileId, 10) || 0,
            }));

          const transformedData: DocumentsResponse = {
            ...networkResponse,
            Records: transformedRecords,
          };

          return { data: transformedData };
        } catch (err) {
          console.error(
            `DOCUMENTS BY ACCOUNT API EXCEPTION for ${accountId}`,
            err,
          );
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (result, _error, { accountId }) =>
        result?.Records
          ? [
              ...result.Records.map(({ id }) => ({
                type: 'Documents' as const,
                id: `account-${accountId}-${id}`,
              })),
              { type: 'Documents', id: `ACCOUNT-LIST-${accountId}` },
            ]
          : [{ type: 'Documents', id: `ACCOUNT-LIST-${accountId}` }],
    }),
    // --- Get Document Folders Query ---
    getDocumentFolders: builder.query<
      DocumentFoldersResponse,
      (GetDocumentFoldersArgs & { InvestorId?: number }) | void
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Base URL (role-aware + optional investor scoping)
        const url = `${buildRoleAwarePrefix(currentRole, args && 'InvestorId' in (args as any) ? (args as any).InvestorId : undefined)}/DocumentFolders`;

        const params: Record<string, any> = {};

        // Handle sort parameters
        if (args?.sort && args.sort.length > 0) {
          args.sort.forEach((sortItem, index) => {
            params[`sort[${index}].Property`] = sortItem.property;
            params[`sort[${index}].Direction`] = sortItem.direction;
            params[`sort[${index}].Order`] = sortItem.order;
          });
        }

        // Add cache busting if provided
        if (args?._) {
          params._ = args._;
        }

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            params,
          });

          if (result.error) {
            return { error: result.error };
          }

          // Normalize to RecordResponse shape. The backend may return a bare
          // array of folders or the { Records, TotalLength } envelope; both
          // are valid. An empty list means the user has no folders — that is
          // a valid response, not an error.
          const raw = result.data as
            | DocumentFoldersResponse
            | DocumentFolderRecord[]
            | undefined;
          let records: DocumentFolderRecord[];
          if (Array.isArray(raw)) {
            records = raw;
          } else if (raw && Array.isArray(raw.Records)) {
            records = raw.Records;
          } else {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'Unexpected data structure from API' },
                error: 'Unexpected data structure',
              } as FetchBaseQueryError,
            };
          }

          return {
            data: {
              Records: records,
              TotalLength: records.length,
              SummedValue: null,
            },
          };
        } catch (err) {
          console.error('DOCUMENT FOLDERS API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      // Provide a tag for the list of folders. Individual folder tagging is complex due to nesting.
      providesTags: (result) =>
        result?.Records
          ? [{ type: 'DocumentFolders', id: 'LIST' }]
          : [{ type: 'DocumentFolders', id: 'LIST' }],
      extraOptions: { maxRetries: 3 },
    }),
    // --- End Get Document Folders Query ---

    // --- Update Document Mutation ---
    updateDocument: builder.mutation<
      UpdateDocumentResponse,
      UpdateDocumentArgs
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const { VaultFileId, InvestorId, ...body } = args; // Extract VaultFileId for URL

        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/documents/${VaultFileId}`;

        // Ensure the body includes VaultFileId as per the cURL example payload
        const requestBody = { ...body, VaultFileId };

        console.log(
          `Attempting to update document: ${VaultFileId} at ${url}`,
          requestBody,
        );

        try {
          const result = await baseQuery({
            url,
            method: 'PUT',
            body: requestBody,
            // Expecting a boolean response, handle accordingly
            responseHandler: async (response) => {
              if (response.ok) {
                try {
                  const data = await response.json();
                  return data === true;
                } catch (e) {
                  // If JSON parsing fails but status is OK, maybe treat as success?
                  // Or return an error if boolean true is strictly expected.
                  console.warn(
                    'Update document response was not JSON boolean true',
                    e,
                  );
                  // Let's strictly expect true based on the example
                  return false; // Or throw an error
                }
              }
              // Let baseQuery handle non-ok responses
              return response.text();
            },
          });

          console.log('UPDATE DOCUMENT API RESULT:', {
            isError: !!result.error,
            urlSent: url,
            responseData: result.data, // Should be true or false
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('UPDATE DOCUMENT API ERROR:', result.error);
            return { error: result.error };
          }

          // Check if the result data is boolean true
          if (result.data !== true) {
            console.warn(
              'UPDATE DOCUMENT API did not return true on success',
              result.data,
            );
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'API did not confirm update successfully',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }

          return { data: true }; // Return true on success
        } catch (err: any) {
          console.error('UPDATE DOCUMENT API EXCEPTION:', err);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { VaultFileId, ParentId }) => [
        { type: 'Documents' as const, id: VaultFileId }, // Invalidate specific document
        { type: 'Documents' as const, id: 'LIST' }, // Invalidate list
        { type: 'DocumentFolders' as const, id: 'LIST' }, // Invalidate folder list
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
        // Add specific parent folder invalidation if needed
        ...(ParentId
          ? [{ type: 'Documents' as const, id: `FOLDER_${ParentId}` }]
          : []),
      ],
    }),
    // --- End Update Document Mutation ---

    // --- "Share Document With": household sharing (EUD-434) ---
    // Investor-only endpoint: WealthAccessApiService exposes it solely as
    // `api/investor/DocumentHouseholdPermissions` (see
    // Areas/Investor/Controllers/DocumentHouseholdPermissionsController.cs);
    // there is no `api/advisor/Investors/{id}/...` counterpart. So the URL is
    // literal and deliberately does NOT go through buildRoleAwarePrefix, and
    // callers must skip it for advisors. That matches legacy, where
    // EditDocument.jsx only hits the store when no `investorId` prop is passed.
    //
    // We fetch the whole list rather than `/{vaultFileId}` per document: the
    // per-document route runs the same two stored procedures across the entire
    // vault and only then filters in memory, so one list call is strictly
    // cheaper and covers the modal's bulk-edit flow in a single request.
    getDocumentHouseholdPermissions: builder.query<
      RecordResponse<DocumentHouseholdPermissionRecord>,
      void
    >({
      queryFn: async (_arg, _api, __, baseQuery) => {
        const result = await baseQuery({
          url: '/api/investor/DocumentHouseholdPermissions',
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        // The backend returns the { Records, TotalLength } envelope; tolerate a
        // bare array too, the same way getDocumentFolders does. An empty list
        // is valid — it just means no document is shared with a household.
        const raw = result.data as
          | RecordResponse<DocumentHouseholdPermissionRecord>
          | DocumentHouseholdPermissionRecord[]
          | null
          | undefined;

        const records: DocumentHouseholdPermissionRecord[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.Records)
            ? raw.Records
            : [];

        return {
          data: {
            Records: records,
            TotalLength: records.length,
            SummedValue: null,
          },
        };
      },
      providesTags: [{ type: 'DocumentHouseholdPermissions', id: 'LIST' }],
    }),

    // The PUT is a full replace, not a delta: the processor collects the
    // InvestorHHIds whose CanSee is false and passes them as @exceptions to
    // uspUpdateInvestorVaultFileHouseholdPermissions. Send every row of the
    // document, not just the ones the user toggled.
    updateDocumentHouseholdPermissions: builder.mutation<
      boolean,
      DocumentHouseholdPermissionRecord
    >({
      queryFn: async (record, _api, __, baseQuery) => {
        const result = await baseQuery({
          url: `/api/investor/DocumentHouseholdPermissions/${String(record.VaultFileId)}`,
          method: 'PUT',
          body: record,
        });

        if (result.error) {
          return { error: result.error };
        }

        if (result.data !== true) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'API did not confirm the household permission update',
              data: result.data,
            } as FetchBaseQueryError,
          };
        }

        return { data: true };
      },
      invalidatesTags: [{ type: 'DocumentHouseholdPermissions', id: 'LIST' }],
    }),
    // --- End "Share Document With" ---

    uploadDocument: builder.mutation<
      UploadDocumentResponse,
      UploadDocumentArgs
    >({
      queryFn: async (
        { file, folderId, accountId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/documents/`;

        const params: Record<string, any> = {};
        if (accountId) {
          params.accountId = accountId;
        }
        if (folderId) {
          params.ParentId = folderId;
        }
        if (typeof InvestorId === 'number') {
          params.investorId = InvestorId;
        }

        const formData = new FormData();
        formData.append('filename', file.name);
        formData.append('file', file, file.name);

        try {
          const result = await baseQuery({
            url,
            method: 'POST',
            body: formData,
            params: params,
          });

          if (result.error) {
            return { error: result.error };
          }
          return { data: result.data as UploadDocumentResponse };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message,
            } as FetchBaseQueryError,
          };
        }
      },
      // Update invalidatesTags to handle both cases
      invalidatesTags: (_result, _error, { folderId, accountId }) => {
        const tags = [
          { type: 'Documents' as const, id: 'LIST' },
          { type: 'Documents2' as const, id: 'LIST' },
        ];

        if (accountId) {
          tags.push({ type: 'Documents', id: `ACCOUNT-LIST-${accountId}` });
        }
        if (folderId) {
          tags.push({ type: 'Documents', id: `FOLDER_${folderId}` });
        }

        return tags;
      },
    }),
    createFolder: builder.mutation<CreateFolderResponse, CreateFolderArgs>({
      queryFn: async (
        { Name, parentFolderId, InvestorTagId, TagId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        // Base URL for creating folders
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/DocumentFolders`;

        const body: {
          Name: string;
          parentId?: string;
          InvestorTagId?: number;
          TagId?: number;
        } = { Name };
        // Optional: Adjust URL if creating within a specific parent folder
        // This depends on the API's structure, e.g., might be part of the URL or body
        if (parentFolderId) {
          // url += `/${parentFolderId}`; // Or modify body
          body.parentId = parentFolderId;
        }
        // An advisor acting on an investor is scoped by the URL, not by tags.
        if (!(isAdvisorRole(currentRole) && typeof InvestorId === 'number')) {
          if (typeof InvestorTagId === 'number') {
            body.InvestorTagId = InvestorTagId;
          }
          if (typeof TagId === 'number') {
            body.TagId = TagId;
          }
        }

        try {
          const result = await baseQuery({
            url,
            method: 'POST',
            body,
          });

          console.log('CREATE FOLDER API RESULT:', {
            isError: !!result.error,
            hasData: !!result.data,
            urlSent: url,
            bodySent: body,
            responseData: result.data, // Log the raw response data
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('CREATE FOLDER API ERROR', result.error);
            return { error: result.error };
          }

          // Check if the response data is the expected ID (string or number)
          if (
            typeof result.data !== 'string' &&
            typeof result.data !== 'number'
          ) {
            console.warn(
              'CREATE FOLDER API RETURNED UNEXPECTED DATA STRUCTURE',
              result.data,
            );
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'Unexpected response data type from API' },
                error: 'Unexpected response data type',
              } as FetchBaseQueryError,
            };
          }

          // Return the ID
          return { data: result.data };
        } catch (err) {
          console.error('CREATE FOLDER API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { parentFolderId }) => [
        { type: 'Documents' as const, id: 'LIST' },
        { type: 'Documents2' as const, id: 'LIST' },
        { type: 'DocumentFolders' as const, id: 'LIST' },
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
        // Also invalidate specific parent folder tag if applicable and implemented
        ...(parentFolderId
          ? [{ type: 'Documents' as const, id: `FOLDER_${parentFolderId}` }]
          : []),
      ],
    }),
    // --- Documents2 Query ---
    getDocuments2: builder.query<Documents2Response, GetDocuments2Args>({
      queryFn: async (
        {
          DateInterval,
          WealthAccountId,
          VaultFileCategoryId,
          FileSourceId,
          SearchTerm,
          AdvisorInvestorId,
          InvestorTagId,
          TagId,
        },
        _queryApi,
        __,
        baseQuery,
      ) => {
        // Always use investor route for Documents2
        const url = `/api/investor/Documents2`;

        // Prepare body as x-www-form-urlencoded
        const bodyParams = new URLSearchParams();
        if (DateInterval !== undefined) {
          bodyParams.append('DateInterval', String(DateInterval));
        }
        bodyParams.append('WealthAccountId', String(WealthAccountId));
        if (typeof AdvisorInvestorId === 'number') {
          bodyParams.append('AdvisorInvestorId', String(AdvisorInvestorId));
        }
        if (typeof InvestorTagId === 'number') {
          bodyParams.append('InvestorTagId', String(InvestorTagId));
        }
        if (typeof TagId === 'number') {
          bodyParams.append('TagId', String(TagId));
        }
        // Append new params if they exist
        if (VaultFileCategoryId !== undefined) {
          bodyParams.append('VaultFileCategoryId', String(VaultFileCategoryId));
        }
        if (FileSourceId !== undefined) {
          bodyParams.append('FileSourceId', String(FileSourceId));
        }
        if (SearchTerm !== undefined) {
          bodyParams.append('SearchTerm', String(SearchTerm));
        }

        try {
          const result = await baseQuery({
            url,
            method: 'POST',
            body: bodyParams.toString(), // Send as string
            headers: {
              'Content-Type':
                'application/x-www-form-urlencoded; charset=UTF-8',
            },
          });

          console.log('DOCUMENTS2 API RESULT:', {
            isError: !!result.error,
            hasData: !!result.data,
            paramsSent: {
              DateInterval,
              WealthAccountId,
              VaultFileCategoryId,
              FileSourceId,
            }, // Log all params
            urlSent: url,
            dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('DOCUMENTS2 API ERROR', result.error);
            return { error: result.error };
          }

          // Check if data is an array as expected
          if (!Array.isArray(result.data)) {
            console.warn(
              'DOCUMENTS2 API RETURNED NON-ARRAY DATA STRUCTURE',
              result.data,
            );
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: { message: 'Unexpected data structure from API' },
                error: 'Unexpected data structure',
              } as FetchBaseQueryError,
            };
          }

          // --- Transformation Step ---
          const rawData = result.data as NetworkDocuments2Response;
          const transformedData: Documents2Response = rawData.map((doc) => ({
            ...doc,
            id: doc.VaultFile2Id, // Add the 'id' property
          }));
          // --- End Transformation Step ---

          return { data: transformedData }; // Return the transformed data
        } catch (err) {
          console.error('DOCUMENTS2 API EXCEPTION:', err);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: String(err) },
              error: String(err),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              // Tag each individual document using the added 'id' or original 'VaultFile2Id'
              ...result.map(({ id }) => ({
                // Use the new 'id' property for tagging
                type: 'Documents2' as const,
                id: id, // Use the mapped id
              })),
              // Tag for the list itself
              { type: 'Documents2', id: 'LIST' },
            ]
          : [{ type: 'Documents2', id: 'LIST' }],
    }),
    // Document Tags
    getDocumentTags: builder.query<
      RecordResponse<any>,
      { InvestorId?: number } | void
    >({
      queryFn: async (args, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const prefix = buildRoleAwarePrefix(currentRole, args?.InvestorId);
        const result = await baseQuery({
          url: `${prefix}/DocumentTags`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as RecordResponse<any> };
      },
      providesTags: (result) =>
        result?.Records
          ? [{ type: 'Documents', id: 'TAGS' }]
          : [{ type: 'Documents', id: 'TAGS' }],
    }),
    // --- End Documents2 Query ---
    getAdvisorRecentDocuments: builder.query<
      AdvisorRecentDocumentRecord[],
      GetAdvisorRecentDocumentsArgs
    >({
      query: ({ StartDate, EndDate }) => ({
        url: '/api/advisor/RecentDocuments',
        method: 'GET',
        params: { StartDate, EndDate },
      }),
      transformResponse: (
        response: RecordResponse<AdvisorRecentDocumentRecord>,
      ) => response.Records,
      providesTags: [{ type: 'Documents', id: 'ADVISOR-RECENT' }],
    }),

    // --- Download Document Mutation ---
    downloadDocument: builder.mutation<
      DownloadDocumentResponse,
      DownloadDocumentArgs
    >({
      queryFn: async (
        { vaultId, filename, statementId, statementName, InvestorId, signal },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const params = new URLSearchParams();
        if (vaultId === -5 && statementId) {
          params.append('StatementId', statementId);
          if (statementName) {
            params.append('StatementName', statementName);
          }
        }
        const query = params.toString();
        const url = `${buildDocuments2FileUrl(currentRole, vaultId, InvestorId)}${query ? `?${query}` : ''}`;

        try {
          console.log(`Attempting to download: ${filename} from ${url}`);
          // Perform the fetch, get the raw Response object
          // Tell baseQuery to expect and handle a blob response
          const result = await baseQuery({
            url: url,
            method: 'GET', // Or "POST" if required by API
            // Use a custom function to handle the response as a blob directly
            responseHandler: handleBlobResponse,
            // We will handle the raw response from meta if status is OK
            signal,
          });

          console.log('DOWNLOAD API RAW RESULT:', {
            isError: !!result.error,
            metaStatus: result.meta?.response?.status,
          });

          // If baseQuery automatically throws or returns an error structure on non-2xx
          if (result.error) {
            console.error('DOWNLOAD API ERROR (from baseQuery):', result.error);
            return { error: result.error };
          }

          // With a custom responseHandler, the successful result (Blob) is in result.data
          // baseQuery handles non-OK status codes and sets result.error automatically.
          try {
            if (Capacitor.getPlatform() === 'web') {
              // Ensure the data is actually a Blob
              if (!(result.data instanceof Blob)) {
                console.error(
                  'DOWNLOAD API did not return a Blob in data',
                  result.data,
                );
                return {
                  error: {
                    status: 'CUSTOM_ERROR',
                    error: 'Invalid data type returned, expected Blob',
                    data: result.data, // Include actual data for debugging
                  } as FetchBaseQueryError,
                };
              }
              // Success: data is a Blob
              console.log(`Successfully fetched blob for: ${filename}`);
            }
            // Trigger the download directly here as a side effect
            try {
              await triggerDocumentDownload(
                result.data as ArrayBuffer,
                filename,
              );
              console.log(`Browser download triggered for: ${filename}`);
              // Return a serializable success payload
              return { data: { success: true, filename: filename } };
            } catch (downloadError) {
              console.error('Error triggering blob download:', downloadError);
              // Return an error if the download trigger itself fails
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Failed to trigger browser download',
                  data: String(downloadError),
                } as FetchBaseQueryError,
              };
            }
          } catch (err) {
            // Catch potential errors if result.data access somehow fails, though unlikely
            console.error('Error processing download result data:', err);
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Failed to process download data',
                data: String(err),
              } as FetchBaseQueryError,
            };
          }
        } catch (err: any) {
          // Catch errors during the baseQuery call itself or other synchronous parts
          console.error('DOWNLOAD API EXCEPTION:', err);
          return {
            error: {
              status: 'FETCH_ERROR', // Indicate a network or fetch-level error
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    // --- End Download Document Mutation ---

    downloadPersonalDocument: builder.mutation<
      DownloadDocumentResponse,
      DownloadDocumentArgs
    >({
      queryFn: async (
        { vaultId, filename, InvestorId, signal },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/documents/${vaultId}`; // v1 endpoint

        try {
          const result = await baseQuery({
            url: url,
            method: 'GET',
            responseHandler: handleBlobResponse,
            signal,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (Capacitor.getPlatform() === 'web') {
            if (!(result.data instanceof Blob)) {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Invalid data type returned, expected Blob',
                } as FetchBaseQueryError,
              };
            }
          }

          await triggerDocumentDownload(result.data as ArrayBuffer, filename);
          return { data: { success: true, filename: filename } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
            } as FetchBaseQueryError,
          };
        }
      },
    }),

    // --- Preview Document Mutation ---
    previewDocument: builder.mutation<
      { success: boolean },
      PreviewDocumentArgs
    >({
      queryFn: async (
        { vaultId, statementId, statementName, InvestorId, fileName },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const params = new URLSearchParams({ IsPreview: 'true' });
        if (vaultId === -5 && statementId) {
          params.append('StatementId', statementId);
          if (statementName) {
            params.append('StatementName', statementName);
          }
        }
        const url = `${buildDocuments2FileUrl(currentRole, vaultId, InvestorId)}?${params.toString()}`;

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            responseHandler: handleBlobResponse,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (Capacitor.getPlatform() === 'web') {
            if (!(result.data instanceof Blob)) {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Response was not a Blob',
                  data: result.data,
                } as FetchBaseQueryError,
              };
            }
          }

          await openDocumentPreview(
            result.data as Blob | ArrayBuffer,
            fileName,
          );

          return { data: { success: true } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    previewPersonalDocument: builder.mutation<
      { success: boolean },
      PreviewDocumentArgs
    >({
      queryFn: async (
        { vaultId, InvestorId, fileName },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        // IMPORTANT: This targets the v1 documents endpoint
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/documents/${vaultId}?IsPreview=true`;

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            responseHandler: handleBlobResponse,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (Capacitor.getPlatform() === 'web') {
            if (!(result.data instanceof Blob)) {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Response was not a Blob',
                  data: result.data,
                } as FetchBaseQueryError,
              };
            }
          }

          await openDocumentPreview(
            result.data as Blob | ArrayBuffer,
            fileName,
          );

          return { data: { success: true } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
    }),

    // --- Preview Advisor Investor Document (v1) ---
    previewAdvisorInvestorDocument: builder.mutation<
      { success: boolean },
      { investorId: number; vaultId: string | number; fileName: string }
    >({
      queryFn: async (
        { investorId, vaultId, fileName },
        _queryApi,
        __,
        baseQuery,
      ) => {
        // Explicit advisor route with investor context and IsPreview=true
        const params = new URLSearchParams({
          investorId: String(investorId),
          IsPreview: 'true',
        });
        const url = `/api/advisor/investors/${investorId}/documents/${vaultId}?${params.toString()}`;

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            responseHandler: handleBlobResponse,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (Capacitor.getPlatform() === 'web') {
            if (!(result.data instanceof Blob)) {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Response was not a Blob',
                  data: result.data,
                } as FetchBaseQueryError,
              };
            }
          }

          await openDocumentPreview(
            result.data as Blob | ArrayBuffer,
            fileName,
          );

          return { data: { success: true } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    // --- Delete Document Mutation ---
    deleteDocument: builder.mutation<
      boolean,
      { vaultId: string | number; InvestorId?: number }
    >({
      queryFn: async ({ vaultId, InvestorId }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/documents/${vaultId}`;

        console.log(
          `Attempting to delete document with ID: ${vaultId} from ${url}`,
        );

        try {
          const result = await baseQuery({
            url: url,
            method: 'DELETE',
            // Expecting a simple boolean or potentially empty response on success
            responseHandler: async (response) => {
              if (response.ok) {
                try {
                  // Try parsing as JSON first, expecting 'true'
                  const data = await response.json();
                  return data === true;
                } catch (e) {
                  // If JSON parsing fails (e.g., empty body), consider 2xx status as success
                  return response.status >= 200 && response.status < 300;
                }
              }
              // Let baseQuery handle non-ok responses to extract error details
              return response.text(); // Return text for baseQuery error handling
            },
          });

          if (result.error) {
            console.error('DELETE DOCUMENT API ERROR:', result.error);
            return { error: result.error };
          }

          // Check if the result data is boolean true
          if (result.data !== true) {
            console.warn(
              'DELETE DOCUMENT API did not return true on success',
              result.data,
            );
            // Return an error if the expected 'true' wasn't received
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'API did not confirm deletion successfully',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }

          return { data: true }; // Return true on success
        } catch (err: any) {
          console.error('DELETE DOCUMENT API EXCEPTION:', err);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { vaultId }) => [
        { type: 'Documents' as const, id: vaultId }, // Invalidate specific document cache
        { type: 'Documents' as const, id: 'LIST' }, // Invalidate the list cache
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
      ],
    }),
    // --- End Delete Document Mutation ---

    // Delete Document For Account
    deleteDocumentForAccount: builder.mutation<
      boolean,
      DeleteDocumentForAccountArgs
    >({
      queryFn: async (
        { accountId, vaultId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/Accounts/${accountId}/documents/${vaultId}`;

        try {
          const result = await baseQuery({
            url: url,
            method: 'DELETE',
            responseHandler: async (response) => {
              if (response.ok) {
                try {
                  const data = await response.json();
                  return data === true;
                } catch (e) {
                  return response.status >= 200 && response.status < 300;
                }
              }
              return response.text();
            },
          });

          if (result.error) {
            return { error: result.error };
          }
          if (result.data !== true) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'API did not confirm deletion successfully',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }
          return { data: true };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { accountId, vaultId }) => [
        { type: 'Documents', id: `account-${accountId}-${vaultId}` },
        { type: 'Documents', id: `ACCOUNT-LIST-${accountId}` },
        { type: 'Documents' as const, id: 'LIST' },
        { type: 'Documents2' as const, id: 'LIST' },
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
      ],
    }),
    // --- Bulk Download Documents Mutation ---
    bulkDownloadDocuments: builder.mutation<
      BulkDownloadDocumentsResponse,
      BulkDownloadDocumentsArgs
    >({
      queryFn: async (
        { vaultFileIds, filename = 'Files.zip', signal },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const token = state.appView.UserInfo?.Token;

        if (!token) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Authentication token not available',
              data: 'User is not authenticated',
            } as FetchBaseQueryError,
          };
        }

        // Note: The token is handled by the baseQuery/axios instance
        const url = `/api/${currentRole}/documentsBulk`;
        const params = new URLSearchParams({
          vaultFileIds: vaultFileIds.join(','),
          filename,
          token,
          CurrentRole: currentRole!,
        });

        try {
          console.log(
            `Attempting bulk download for IDs: ${vaultFileIds.join(
              ',',
            )} from ${url}`,
          );

          const result = await baseQuery({
            url: `${url}?${params.toString()}`, // Append params to URL for GET request
            method: 'GET',
            responseHandler: handleBlobResponse, // Expect a blob response
            // No body for GET request
            signal,
          });

          console.log('BULK DOWNLOAD API RAW RESULT:', {
            isError: !!result.error,
            metaStatus: result.meta?.response?.status,
          });

          if (result.error) {
            console.error('BULK DOWNLOAD API ERROR:', result.error);
            return { error: result.error };
          }

          // With a custom responseHandler, the successful result (Blob) is in result.data
          try {
            if (Capacitor.getPlatform() === 'web') {
              if (!(result.data instanceof Blob)) {
                console.error(
                  'BULK DOWNLOAD API did not return a Blob',
                  result.data,
                );
                return {
                  error: {
                    status: 'CUSTOM_ERROR',
                    error: 'Invalid data type returned, expected Blob',
                    data: result.data,
                  } as FetchBaseQueryError,
                };
              }
            }

            const downloadData =
              result.data instanceof Blob
                ? result.data
                : (result.data as ArrayBuffer);

            console.log(
              `Successfully fetched blob for bulk download: ${filename}`,
            );

            try {
              await triggerDocumentDownload(downloadData, filename);
              console.log(`Browser download triggered for: ${filename}`);
              return { data: { success: true, filename: filename } };
            } catch (downloadError) {
              console.error('Error triggering blob download:', downloadError);
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Failed to trigger browser download',
                  data: String(downloadError),
                } as FetchBaseQueryError,
              };
            }
          } catch (err) {
            console.error('Error processing bulk download result data:', err);
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Failed to process bulk download data',
                data: String(err),
              } as FetchBaseQueryError,
            };
          }
        } catch (err: any) {
          console.error('BULK DOWNLOAD API EXCEPTION:', err);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
      // Bulk download doesn't invalidate specific tags in the same way as single actions might.
      // It's primarily a read operation resulting in a download side-effect.
      // No invalidation tags needed unless there's a specific reason.
    }),
    // --- End Bulk Download Documents Mutation ---

    // --- Bulk Download Documents2 Mutation ---
    bulkDownloadDocuments2: builder.mutation<
      BulkDownloadDocumentsResponse,
      BulkDownloadDocuments2Args
    >({
      queryFn: async (
        {
          vaultFileIds,
          personalVaultFileIds,
          externalFiles,
          filename = 'Files.zip',
          investorId,
          signal,
        },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const token = state.appView.UserInfo?.Token;
        const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';
        const basePath =
          isAdvisor && typeof investorId === 'number'
            ? `/api/advisor/investors/${investorId}/Documents2`
            : '/api/investor/Documents2';

        const params = new URLSearchParams();
        params.append('VaultFileIds', vaultFileIds.join(','));
        if (personalVaultFileIds) {
          params.append('PersonalVaultFileIds', personalVaultFileIds.join(','));
        }
        externalFiles?.forEach((file, index) => {
          const key = `ExternalFiles[${String(index)}]`;
          params.append(`${key}.ExternalFileId`, file.externalFileId);
          params.append(`${key}.ExternalFileName`, file.externalFileName);
        });
        if (token) {
          params.append('token', token);
        }
        if (currentRole) {
          params.append('CurrentRole', currentRole);
        }
        if (isAdvisor && typeof investorId === 'number') {
          params.append('AdvisorInvestorId', String(investorId));
        }

        try {
          const result = await baseQuery({
            url: `${basePath}?${params.toString()}`,
            method: 'GET',
            responseHandler: handleBlobResponse,
            signal,
          });

          if (result.error) {
            return { error: result.error };
          }

          if (Capacitor.getPlatform() === 'web') {
            if (!(result.data instanceof Blob)) {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Invalid data type returned, expected Blob',
                  data: result.data,
                } as FetchBaseQueryError,
              };
            }
          }

          const downloadData =
            result.data instanceof Blob
              ? result.data
              : (result.data as ArrayBuffer);

          try {
            await triggerDocumentDownload(downloadData, filename);
            return { data: { success: true, filename } };
          } catch (downloadError) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Failed to trigger browser download',
                data: String(downloadError),
              } as FetchBaseQueryError,
            };
          }
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    // --- End Bulk Download Documents2 Mutation ---

    // --- Bulk Download Documents Mutation (Advisor-specific) ---
    bulkDownloadAdvisorDocuments: builder.mutation<
      BulkDownloadDocumentsResponse,
      {
        investorId: number;
        vaultFileIds: string[];
        filename?: string;
        signal?: AbortSignal;
      }
    >({
      queryFn: async (
        { investorId, vaultFileIds, filename = 'Files.zip' },
        { getState, signal },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const token = state.appView.UserInfo?.Token;

        if (!token) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Authentication token not available',
              data: 'User is not authenticated',
            } as FetchBaseQueryError,
          };
        }

        const url = `/api/advisor/investors/${investorId}/documentsBulk/`;
        const params = new URLSearchParams({
          vaultFileIds: vaultFileIds.join(','),
          token,
          CurrentRole: currentRole!,
        });

        try {
          console.log(
            `Attempting advisor bulk download for investor ${investorId} IDs: ${vaultFileIds.join(
              ',',
            )} from ${url}`,
          );

          const result = await baseQuery({
            url: `${url}?${params.toString()}`,
            method: 'GET',
            responseHandler: handleBlobResponse,
            signal,
          });

          if (result.error) {
            console.error('ADVISOR BULK DOWNLOAD API ERROR:', result.error);
            return { error: result.error };
          }

          try {
            if (Capacitor.getPlatform() === 'web') {
              if (!(result.data instanceof Blob)) {
                return {
                  error: {
                    status: 'CUSTOM_ERROR',
                    error: 'Invalid data type returned, expected Blob',
                    data: result.data,
                  } as FetchBaseQueryError,
                };
              }
            }

            const downloadData =
              result.data instanceof Blob
                ? result.data
                : (result.data as ArrayBuffer);

            await triggerDocumentDownload(downloadData, filename);
            return { data: { success: true, filename } };
          } catch (err) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Failed to process bulk download data',
                data: String(err),
              } as FetchBaseQueryError,
            };
          }
        } catch (err) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error:
                (err as Error & { message?: string }).message || String(err),
              data: (err as Error & { stack?: string }).stack,
            } as FetchBaseQueryError,
          };
        }
      },
      // Read-only operation with download side effect; no cache invalidation required
    }),
    // --- End Bulk Download Documents Mutation (Advisor-specific) ---

    // --- NEW: Delete Folder Mutation ---
    deleteFolder: builder.mutation<
      boolean,
      { folderId: string | number; InvestorId?: number }
    >({
      queryFn: async (
        { folderId, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/DocumentFolders/${folderId}`;

        console.log(
          `Attempting to delete folder with ID: ${folderId} from ${url}`,
        );

        try {
          const result = await baseQuery({
            url: url,
            method: 'DELETE',
            responseHandler: async (response) => {
              if (!response.ok) return false;
              // DELETE often returns 204 No Content or 200 OK with a simple body (like 'true')
              // If it returns 'true'/'false' as text/json:
              try {
                const data = await response.json(); // or response.text() if plain text
                return data === true;
              } catch (e) {
                // If body is empty (204) or not 'true', consider it success if response.ok
                return response.ok;
              }
            },
          });

          console.log('DELETE FOLDER API RESULT:', {
            isError: !!result.error,
            urlSent: url,
            responseData: result.data, // Should be true or false
            resultMeta: {
              status: result.meta?.response?.status,
              statusText: result.meta?.response?.statusText,
            },
          });

          if (result.error) {
            console.error('DELETE FOLDER API ERROR:', result.error);
            return { error: result.error };
          }

          // Check if the result data is boolean true
          if (result.data !== true) {
            console.warn(
              'DELETE FOLDER API did not return true on success',
              result.data,
            );
            // Return an error if the expected 'true' wasn't received
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'API did not confirm deletion successfully',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }

          return { data: true }; // Return true on success
        } catch (err: any) {
          console.error('DELETE FOLDER API EXCEPTION:', err);
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message || String(err),
              data: err.stack,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { folderId }) => [
        { type: 'Documents' as const, id: 'LIST' },
        { type: 'Documents2' as const, id: 'LIST' },
        { type: 'DocumentFolders' as const, id: 'LIST' },
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
        // Invalidate specific folder if we have folder tagging
        { type: 'Documents' as const, id: `FOLDER_${folderId}` },
      ],
    }),
    // Update Document For Account
    updateDocumentForAccount: builder.mutation<
      UpdateDocumentResponse,
      UpdateDocumentForAccountArgs
    >({
      queryFn: async ({ accountId, ...args }, { getState }, __, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const { VaultFileId, ...body } = args;
        const url = `/api/${currentRole}/Accounts/${accountId}/documents/${VaultFileId}`;
        const requestBody = { ...body, VaultFileId };

        try {
          const result = await baseQuery({
            url,
            method: 'PUT',
            body: requestBody,
            responseHandler: async (response) => {
              if (response.ok) {
                try {
                  return await response.json();
                } catch (e) {
                  return response.ok;
                }
              }
              return response.text();
            },
          });

          if (result.error) {
            return { error: result.error };
          }
          if (result.data !== true) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'API did not confirm update successfully',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }
          return { data: true };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message,
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (_result, _error, { accountId, VaultFileId }) => [
        { type: 'Documents', id: `account-${accountId}-${VaultFileId}` },
        { type: 'Documents', id: `ACCOUNT-LIST-${accountId}` },
        { type: 'Documents' as const, id: 'LIST' },
        { type: 'Documents2' as const, id: 'LIST' },
        { type: 'DocumentFolders' as const, id: 'LIST' },
        // Invalidate all Documents2 cache entries regardless of specific parameters
        { type: 'Documents2' as const },
        // Invalidate all Documents cache entries regardless of specific parameters
        { type: 'Documents' as const },
      ],
    }),
    getDocumentsCountByAccountId: builder.query<DocumentsCountResponse, number>(
      {
        query: (accountId) => ({
          url: `api/investor/Accounts/${accountId}/documents/count`,
        }),
        providesTags: (_result, _error, accountId) => [
          { type: 'Documents', id: `ACCOUNT-LIST-${accountId}` },
        ],
      },
    ),
    previewAccountDocument: builder.mutation<
      { success: boolean },
      PreviewAccountDocumentArgs
    >({
      queryFn: async (
        { wealthAccountId, vaultId, fileName, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        // This constructs the correct, account-specific URL
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/Accounts/${wealthAccountId}/documents/${vaultId}?IsPreview=true`;

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            responseHandler: handleBlobResponse,
          });
          if (result.error) {
            return { error: result.error };
          }
          if (!(result.data instanceof Blob)) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Response was not a Blob',
                data: result.data,
              } as FetchBaseQueryError,
            };
          }

          await openDocumentPreview(result.data, fileName);

          return { data: { success: true } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
    downloadAccountDocument: builder.mutation<
      DownloadDocumentResponse,
      DownloadAccountDocumentArgs
    >({
      queryFn: async (
        { wealthAccountId, vaultId, filename, InvestorId },
        { getState },
        __,
        baseQuery,
      ) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        // This constructs the correct, account-specific URL for downloading
        const url = `${buildRoleAwarePrefix(currentRole, InvestorId)}/Accounts/${wealthAccountId}/documents/${vaultId}`;

        try {
          const result = await baseQuery({
            url,
            method: 'GET',
            responseHandler: handleBlobResponse,
          });

          if (result.error) {
            return { error: result.error };
          }

          await triggerDocumentDownload(result.data as ArrayBuffer, filename);

          return { data: { success: true, filename: filename } };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: err.message,
            } as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
});

export const {
  useGetDocumentsQuery: useGetDocumentsQueryBase,
  useGetDocumentFoldersQuery,
  useUpdateDocumentMutation,
  useGetDocumentHouseholdPermissionsQuery,
  useUpdateDocumentHouseholdPermissionsMutation,
  useUploadDocumentMutation,
  useCreateFolderMutation,
  useGetDocuments2Query: useGetDocuments2QueryBase,
  useDownloadDocumentMutation,
  usePreviewDocumentMutation,
  useGetDocumentTagsQuery,
  useGetDocumentsHistoryQuery,
  usePreviewPersonalDocumentMutation,
  useDeleteDocumentMutation,
  useBulkDownloadDocumentsMutation,
  useBulkDownloadDocuments2Mutation,
  useDeleteFolderMutation,
  useGetDocumentsByAccountIdQuery,
  useGetDocumentsCountByAccountIdQuery,
  useDeleteDocumentForAccountMutation,
  useUpdateDocumentForAccountMutation,
  useDownloadPersonalDocumentMutation,
  usePreviewAccountDocumentMutation,
  useDownloadAccountDocumentMutation,
  useBulkDownloadAdvisorDocumentsMutation,
  usePreviewAdvisorInvestorDocumentMutation,
  useGetAdvisorRecentDocumentsQuery,
} = documentsApi;

// `ReturnType` of the generic base hook collapses to `any`; pin the result type
// and accept only subscription options, as holdingsApi does.
type GetDocumentsUseQueryOptions = TypedUseQuerySubscriptionOptions;

type GetDocumentsQueryResult = TypedUseQueryHookResult<
  DocumentsResponse,
  GetDocumentsArgs | void,
  typeof waServicesQuery
>;

type GetDocuments2UseQueryOptions = Parameters<
  typeof documentsApi.endpoints.getDocuments2.useQuery
>[1];

export function useGetDocumentsQuery(
  arg: GetDocumentsArgs | typeof skipToken,
  options?: GetDocumentsUseQueryOptions,
): GetDocumentsQueryResult {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildDocumentsSubscriptionArg(arg, confirmed.selection);

  return documentsApi.endpoints.getDocuments.useQuery(queryArg, options);
}

export function useGetDocuments2Query(
  arg: GetDocuments2Args | typeof skipToken,
  options?: GetDocuments2UseQueryOptions,
): ReturnType<typeof useGetDocuments2QueryBase> {
  const confirmed = useSelector(selectQueryViewingTag);

  const queryArg =
    arg === skipToken || !confirmed.ready
      ? skipToken
      : buildDocuments2SubscriptionArg(arg, confirmed.selection);

  return documentsApi.endpoints.getDocuments2.useQuery(queryArg, options);
}
